import React, { useEffect, useState, useCallback } from "react";
import { useRbac } from "./RbacContext";
import jsonSchema from "./edit-user-schema.json";
import uiSchema from "./edit-user-uischema.json";

import {
  Dropdown,
  DropdownOption,
  Icon,
  Input,
  Button,
  ToggleSwitch,
  MessageDialog,
} from "@abb/abb-common-ux-react";


import { useNavigate } from "react-router-dom";
import "./edit-user.css";
import { SUBSTATION_ROLES,BASE_URL, MESSAGES } from "../../strings";
import { validateJsonSchema,isJsonValidSchema } from "../../util";
import apiRequest from "../../config/api";

const EditUser = () => {
  // Helper function to check if user has a specific role
  // This enables role-agnostic permission checks in JSX
  const hasRole = (editUserRole, roleToCheck) => {
    return editUserRole === roleToCheck;
  };

  // Get role values from constants
  const adminRole = SUBSTATION_ROLES[0].value; // OperationalManager
  const technicianRole = SUBSTATION_ROLES[2].value; // Technician

  const commonFlags = {
    clearable: true,
    searchable: true,
    clearOnEscape: true,
    sizeClass: "small",
  };

  const [currentUrl] = useState(window.location.hash);
  const emailRegex =
    /\/edit-user\/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
  const match = emailRegex.exec(currentUrl);
  const currentUserEmailId = match ? match[1] : null;
  //Permissions state
  const [permissions, setPermissions] = React.useState({
    userManagement: false,
    substationExplorer: false,
    addSubstationMapping: false,
    uploadSingleLineDiagram: false,
    disturbanceRecordAcknowledgment: false,
    disturbanceRecordMobileNotification: false,
    disturbanceRecordEscalationNotification: false,
    caesMLFeedback: false,
  });
  //Dropdowns
  const [substationTableData, setSubstationTableData] = React.useState([]);
  const [opsPlace, setOpsPlace] = React.useState([]);
  const [opsSelectedPlace, setOpsSelectedPlace] = React.useState([]);
  const [isSetFetchOps, setIsSetFetchOps] = useState(false);

  //API Set vars
  const [editUserDetails, setEditUserDetails] = React.useState();
  const [error, setError] = useState(false);
  const [filteredSubstations, setFilteredSubstations] = useState([]);
  const [isSaveClick, setIsSaveClick] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { rbacData, fetchRbacData, setRbacData } = useRbac();

  //Save button in form
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  //Dropdown
  const [dpEdited, setDpEdited] = useState(false);

  const formData = {
    name: `${editUserDetails?.givenName} ${editUserDetails?.familyName}`,
    email: editUserDetails?.email,
    currentLocation: editUserDetails?.address,
    contactNumber: editUserDetails?.phoneNumber,
    activeStatus: editUserDetails?.activeStatus || false,
    role: editUserDetails?.role || "",
  };

  //Navigation
  const navigate = useNavigate();
  //validation of JSON
  useEffect(() => {
    const jsonString = JSON.stringify(jsonSchema);

    if (isJsonValidSchema(jsonString)) {
      validateJsonSchema(jsonSchema);
    } else {
      setError("Invalid JSON syntax: Missing braces or commas.");
    }
  }, []);
  //form inputs
  // get from local storage and set to state from rbacManagerUsersList instead of USER_INFO
  // to support other users permissons updation on save
  let localManagerUsersList;
  try {
    const data = localStorage.getItem("rbacManagerUsersList");
    localManagerUsersList = data ? JSON.parse(data) : [];
  } catch (error) {
    console.error("Error parsing rbacManagerUsersList:", error);
    localManagerUsersList = []; // Default to an empty array
  }

  const currentUser = localManagerUsersList.find(
    (user) => user.email === currentUserEmailId
  );

  // Track if permissions have changed from their original values
  const [originalPermissions, setOriginalPermissions] = useState({});
  
  useEffect(() => {
    if (originalPermissions && Object.keys(originalPermissions).length > 0) {
      const havePermissionsChanged = Object.keys(permissions).some(
        (key) => permissions[key] !== originalPermissions[key]
      );
      setIsSaveEnabled(dpEdited || havePermissionsChanged);
    }
  }, [dpEdited, permissions, originalPermissions]);
  const goToSettings = () => {
    navigate("../settings");
  };

  const goToSettingsFromSave = useCallback(async () => {
    if (!rbacData) {
      await fetchRbacData();
    }
    navigate("../../settings");
  }, [rbacData, fetchRbacData, navigate]);

  useEffect(() => {
    if (!rbacData) {
      const localData = localStorage.getItem("rbacManagerUsersList");
      if (localData) {
        setRbacData(JSON.parse(localData));
      }
    }
  }, [rbacData, setRbacData]);

  const fetchoperationAreasData = async () => {
    try {
      const data = await apiRequest({
        method: "GET",
        endPoint: "substation-details",
        baseURL: BASE_URL,
      });

      let duplicateOperationAreas = [];
      data.forEach((element) => {
        duplicateOperationAreas.push(element?.operationArea);
      });
      let uniqueoperationAreas = [...new Set(duplicateOperationAreas)];
      setSubstationTableData(data);
      setOpsPlace(uniqueoperationAreas);
    } catch (error) {
      console.error(
        "Error fetching operation areas data:",
        error.message || error
      );
    }
  };

  function filterAndExtract(substations, filterAreas) {
    const filteredSubstations = substations
      .filter((sub) => filterAreas.includes(sub.operationArea))
      .map((sub) => ({ substationId: sub.substationId, name: sub.name }));

    return filteredSubstations;
  }
  const getSubstations = (substations, opsArea) => {
    let selectedops = [];
    opsArea?.forEach((element) => {
      selectedops.push(element?.value);
    });
    const filteredSubstations = filterAndExtract(substations, selectedops);
    setFilteredSubstations(filteredSubstations);
  };

  // Helper function to parse permission values (handles both string and boolean)
  const parsePermissionValue = (value) => {
    if (typeof value === 'string') {
      return JSON.parse(value);
    }
    return value ?? false;
  };

  useEffect(() => {
    fetchEditUserData();
  }, [isSetFetchOps]);

  const fetchEditUserData = async () => {
    try {
      const data = await apiRequest({
        method: "GET",
        endPoint: "rbac-get",
        baseURL: BASE_URL,
      });
      localStorage.setItem(
        "rbacManagerUsersList",
        JSON.stringify(data)
      );
      const localManagerUsersList = JSON.parse(
        localStorage.getItem("rbacManagerUsersList")
      );
      const currentUser = localManagerUsersList.find(
        (user) => user.email === currentUserEmailId
      );
      let fetchdetails = currentUser;
      await fetchoperationAreasData();
      setEditUserDetails(fetchdetails);
      const usersFetchedOpsarea = fetchdetails?.operationAreas;
      if (!usersFetchedOpsarea.includes("")) {
        const formatedOpsAreas = usersFetchedOpsarea.map((area) => ({
          value: area,
          label: area,
          isNew: false,
        }));
        setOpsSelectedPlace(formatedOpsAreas);
        getSubstations(substationTableData, formatedOpsAreas);
      }
      setIsSetFetchOps(true);
      setError(false);
      const userPermission = fetchdetails?.permissions;
      // Set all permissions at once, handling both string and boolean values
      const parsedPermissions = {
        userManagement: parsePermissionValue(userPermission?.userManagement),
        substationExplorer: parsePermissionValue(userPermission?.substationExplorer),
        addSubstationMapping: parsePermissionValue(userPermission?.addSubstationMapping),
        uploadSingleLineDiagram: parsePermissionValue(userPermission?.uploadSingleLineDiagram),
        disturbanceRecordAcknowledgment: parsePermissionValue(userPermission?.disturbanceRecordAcknowledgment),
        disturbanceRecordMobileNotification: parsePermissionValue(userPermission?.disturbanceRecordMobileNotification),
        disturbanceRecordEscalationNotification: parsePermissionValue(userPermission?.disturbanceRecordEscalationNotification),
        caesMLFeedback: parsePermissionValue(userPermission?.caesMLFeedback),
      };
      setPermissions(parsedPermissions);
      setOriginalPermissions(parsedPermissions);
    } catch (error) {
      console.error(error);
      setError(true);
    }
  };

  // Create the final JSON object
  const SaveOrUpdateUserDetails = async () => {
    // Validation: Check if all required fields are populated
    if (!formData.name || !formData.email) {
      setError(true);
      return;
    }
    
    const updatedUserInfoAsBody = {
      ...editUserDetails,
      operationAreas: opsSelectedPlace?.map((item) => item?.value),
      substations: filteredSubstations?.map((item) => item.name),
      permissions: permissions,
    };

    const existingLocalStorageUserInfo = JSON.parse(
      localStorage.getItem("USER_INFO")
    );
    if (existingLocalStorageUserInfo?.email === currentUserEmailId) {
      const updatedLocalStorageUserInfo = {
        ...existingLocalStorageUserInfo,
        operationAreas: opsSelectedPlace?.map((item) => item?.value),
        substations: filteredSubstations?.map((item) => item.name),
        permissions: permissions,
      };
      localStorage.setItem(
        "USER_INFO",
        JSON.stringify(updatedLocalStorageUserInfo)
      );
    } else {
      const rbacManagerUsersList =
        JSON.parse(localStorage.getItem("rbacManagerUsersList")) || [];
      const updatedRbacManagerUsersList = rbacManagerUsersList.map((user) =>
        user.email === currentUserEmailId
          ? {
            ...user,
            operationAreas: opsSelectedPlace?.map((item) => item?.value),
            substations: filteredSubstations?.map((item) => item.name),
            permissions: permissions,
          }
          : user
      );
      localStorage.setItem(
        "rbacManagerUsersList",
        JSON.stringify(updatedRbacManagerUsersList)
      );
    }

    try {
      await apiRequest({
        method: "PUT",
        endPoint: "rbac-put",
        baseURL: BASE_URL,
        data: updatedUserInfoAsBody,
      });
      setIsSaveClick(false);
      setIsSaved(true);
      fetchEditUserData();
      fetchRbacData();
      // fetch entire list and set to localhistory
    } catch (error) {
      setIsSaveClick(false);
      console.error(error);
      setError(true);
    }
  };

  function handleSaveUserInfo() {
    setIsSaveClick(true);
  }

  const userRole = editUserDetails?.role;

  // Extract form fields rendering to reduce complexity
  const renderFormFields = () => (
    <>
      <tr>
        {jsonSchema.properties.name.enabled && (
          <td>
            <Input
              label={jsonSchema.properties.name.title}
              sizeClass={uiSchema.name["ui:options"].sizeClass}
              value={formData.name}
              style={uiSchema.name["ui:options"].style}
            />
          </td>
        )}
        {jsonSchema.properties.email.enabled && (
          <td>
            <Input
              label={jsonSchema.properties.email.title}
              sizeClass={uiSchema.email["ui:options"].sizeClass}
              value={formData.email}
              style={uiSchema.email["ui:options"].style}
            />
          </td>
        )}
      </tr>
      <tr>
        {jsonSchema.properties.currentLocation.enabled && (
          <td className="edit-user-col">
            <Input
              label={jsonSchema.properties.currentLocation.title}
              sizeClass={uiSchema.currentLocation["ui:options"].sizeClass}
              value={formData.currentLocation}
              style={uiSchema.currentLocation["ui:options"].style}
            />
          </td>
        )}
        {jsonSchema.properties.contactNumber.enabled && (
          <td className="edit-user-col">
            <Input
              label={jsonSchema.properties.contactNumber.title}
              sizeClass={uiSchema.contactNumber["ui:options"].sizeClass}
              value={formData.contactNumber}
              style={uiSchema.contactNumber["ui:options"].style}
            />
          </td>
        )}
      </tr>
      {jsonSchema.properties.userStatus.enabled && (
        <tr>
          <td className="edit-user-col">
            <span>{jsonSchema.properties.userStatus.title}</span>
            <br />
            <span>
              {editUserDetails?.activeStatus ? "Enabled" : "Disabled"}
            </span>
          </td>
        </tr>
      )}
    </>
  );

  const renderRoleAndOperationArea = () => (
    <>
      {jsonSchema.properties.rolesAndOperation.enabled && (
        <tr>
          <td className="edit-user-col">
            <b>{jsonSchema.properties.rolesAndOperation.title}</b>
          </td>
        </tr>
      )}
      <tr style={{ display: "flex", marginTop: "-8px" }}>
        {jsonSchema.properties.role.enabled && (
          <td className="edit-user-col">
            <Input
              label={jsonSchema.properties.role.title}
              sizeClass={uiSchema.role["ui:options"].sizeClass}
              style={uiSchema.role["ui:options"].style}
              value={editUserDetails?.role}
            />
          </td>
        )}
        {jsonSchema.properties.operationArea.enabled && (
          <td>
            <Dropdown
              name="opsarea"
              className={`drop-ops-area ${uiSchema.opsarea["ui:classNames"]}`}
              style={{ paddingLeft: "16px" }}
              label={jsonSchema.properties.operationArea.title}
              monochrome={!uiSchema.opsarea["ui:options"].label}
              edited={dpEdited}
              multiselect={true}
              value={opsSelectedPlace}
              onChange={(val) => {
                setDpEdited(true);
                setOpsSelectedPlace(val);
                getSubstations(substationTableData, val);
              }}
              initialOpen="multi"
              {...commonFlags}
            >
              {opsPlace.map((option) => (
                <DropdownOption
                  key={option}
                  label={option}
                  value={option}
                />
              ))}
            </Dropdown>
          </td>
        )}
      </tr>
    </>
  );

  const renderPermissionToggles = () => (
    <>
      {jsonSchema.properties.setPermission.enabled && (
        <tr>
          <td className="edit-user-col">
            <b>{jsonSchema.properties.setPermission.title}</b>
          </td>
        </tr>
      )}
      {jsonSchema.properties.modifyuser.enabled && (
        <tr>
          <td className="edit-user-col">
            <span>{jsonSchema.properties.modifyuser.title}</span>
            <ToggleSwitch
              name="modifyuser"
              label={
                permissions.userManagement
                  ? jsonSchema.properties.modifyuser.labels.true
                  : jsonSchema.properties.modifyuser.labels.false
              }
              value={permissions.userManagement}
              onChange={(newState) => {
                setPermissions({ ...permissions, userManagement: newState });
              }}
              disabled={!hasRole(userRole, adminRole)}
            />
          </td>
        </tr>
      )}
      {jsonSchema.properties.caesml.enabled && (
        <td className="edit-user-col">
          <span>{jsonSchema.properties.caesml.title}</span>
          <ToggleSwitch
            name="caesml"
            label={
              permissions.caesMLFeedback
                ? jsonSchema.properties.caesml.labels.true
                : jsonSchema.properties.caesml.labels.false
            }
            value={permissions.caesMLFeedback}
            onChange={(newState) => {
              setPermissions({ ...permissions, caesMLFeedback: newState });
            }}
            disabled={uiSchema.caesml["ui:disabled"]}
          />
        </td>
      )}
      {jsonSchema.properties.accesmap.enabled && (
        <tr>
          <td className="edit-user-col">
            <span>{jsonSchema.properties.accesmap.title}</span>
            <ToggleSwitch
              name="accesmap"
              label={
                permissions.addSubstationMapping
                  ? jsonSchema.properties.accesmap.labels.true
                  : jsonSchema.properties.accesmap.labels.false
              }
              value={permissions.addSubstationMapping}
              onChange={(newState) => {
                setPermissions({ ...permissions, addSubstationMapping: newState });
              }}
              disabled={!hasRole(userRole, adminRole)}
            />
          </td>
        </tr>
      )}
      {jsonSchema.properties.accesexp.enabled && (
        <tr>
          <td className="edit-user-col">
            <span>{jsonSchema.properties.accesexp.title}</span>
            <ToggleSwitch
              name="accesexp"
              label={
                permissions.substationExplorer
                  ? jsonSchema.properties.accesexp.labels.true
                  : jsonSchema.properties.accesexp.labels.false
              }
              value={permissions.substationExplorer}
              onChange={(newState) => {
                setPermissions({ ...permissions, substationExplorer: newState });
              }}
              disabled={uiSchema.accesexp["ui:disabled"]}
            />
          </td>
        </tr>
      )}
      {jsonSchema.properties.updatesld.enabled && (
        <tr>
          <td className="edit-user-col">
            <span>{jsonSchema.properties.updatesld.title}</span>
            <ToggleSwitch
              name="updatesld"
              label={
                permissions.uploadSingleLineDiagram
                  ? jsonSchema.properties.updatesld.labels.true
                  : jsonSchema.properties.updatesld.labels.false
              }
              value={permissions.uploadSingleLineDiagram}
              onChange={(newState) => {
                setPermissions({ ...permissions, uploadSingleLineDiagram: newState });
              }}
              disabled={hasRole(userRole, technicianRole)}
            />
          </td>
        </tr>
      )}
      {jsonSchema.properties.ackdist.enabled && (
        <tr>
          <td className="edit-user-col">
            <span>{jsonSchema.properties.ackdist.title}</span>
            <ToggleSwitch
              name="ackdist"
              label={
                permissions.disturbanceRecordAcknowledgment
                  ? jsonSchema.properties.ackdist.labels.true
                  : jsonSchema.properties.ackdist.labels.false
              }
              value={permissions.disturbanceRecordAcknowledgment}
              onChange={(newState) => {
                setPermissions({ ...permissions, disturbanceRecordAcknowledgment: newState });
              }}
            />
          </td>
        </tr>
      )}
      {jsonSchema.properties.drescalation.enabled && (
        <tr>
          <td className="edit-user-col">
            <span>{jsonSchema.properties.drescalation.title}</span>
            <ToggleSwitch
              name="drescalation"
              label={
                permissions.disturbanceRecordEscalationNotification
                  ? jsonSchema.properties.drescalation.labels.true
                  : jsonSchema.properties.drescalation.labels.false
              }
              value={permissions.disturbanceRecordEscalationNotification}
              onChange={(newState) => {
                setPermissions({ ...permissions, disturbanceRecordEscalationNotification: newState });
              }}
              disabled={!hasRole(userRole, adminRole)}
            />
          </td>
        </tr>
      )}
    </>
  );

  return (
    <>
      {isSaveClick ? (
        <div className="overlay-dr">
          <div className="dialog-containe-edit-user">
            {" "}
            <MessageDialog
              title={MESSAGES.CONFIRM_USER_TITLE}
              message={MESSAGES.CONFIRM_USER_BODY}
              className="save-confirm-dialog-container"
              buttons="yesno"
              icon="abb/settings"
              onExit={(v) => setIsSaveClick(false)}
              onYes={() => {
                SaveOrUpdateUserDetails();
                goToSettingsFromSave(); // Navigate after saving
              }}
              onNo={() => setIsSaveClick(false)}
            />
          </div>{" "}
        </div>
      ) : (
        ""
      )}
      <div className="edit-user-container">
        <button
          style={{
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
          }}
          onClick={goToSettings}
          aria-label="Go back to settings"
        >
          <span>
            <Icon sizeClass="small" name="abb/left-arrow" />
          </span>
          {"  "}
          <span>Back</span>
        </button>

        {jsonSchema.enabled && (
          <p className="edit-user-header">
            {jsonSchema.title} {editUserDetails?.givenName}{" "}
            {editUserDetails?.familyName}
          </p>
        )}
        {jsonSchema.properties.enabled && (
          <p className="edit-user-sub-header">
            {jsonSchema.properties.title.title}
          </p>
        )}
        <table id="edit-user-table">
          {renderFormFields()}
          {renderRoleAndOperationArea()}
          {jsonSchema.properties.availableSubstation.enabled && (
            <tr>
              <td className="edit-user-col">
                <span>{jsonSchema.properties.availableSubstation.title}</span>
              </td>
            </tr>
          )}
          {jsonSchema.properties.availableSubstation.enabled && (
            <tr>
              <div style={{ maxWidth: "700px" }}>
                {filteredSubstations.map((substationItem) => {
                  return (
                    <span
                      key={substationItem.id || substationItem.name}
                      className="avail-substn"
                    >
                      {substationItem.name}
                    </span>
                  );
                })}
              </div>
            </tr>
          )}
          {renderPermissionToggles()}
          <tr>
            <td className="edit-user-col">
              {jsonSchema.properties.SaveButton.enabled && (
                <Button
                  text={jsonSchema.properties.SaveButton.const}
                  disabled={!isSaveEnabled}
                  shape={"pill"}
                  sizeClass={"small"}
                  type={"primary-black"}
                  onClick={() => handleSaveUserInfo()}
                />
              )}

              {jsonSchema.properties.CancelButton.enabled && (
                <Button
                  text={jsonSchema.properties.CancelButton.const}
                  shape={"pill"}
                  sizeClass={"small"}
                  onClick={goToSettingsFromSave}
                />
              )}
            </td>
          </tr>
        </table>
      </div>
    </>
  );
};

export default EditUser;